import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Briefcase, Download, ExternalLink, KeyRound, LogOut, Mail, Send } from "lucide-react";
import { toast } from "sonner";

import {
  listPartnerDeals,
  listPartnerForms,
  listPartnerResources,
  loadPartnerMe,
  partnerLogout,
  requestPartnerLogin,
  trackResourceDownload,
  type PartnerDeal,
  type PartnerResource,
  type PartnerSummary,
  listPartnerSubmissions,
} from "@/lib/partner-api";
import { PortalShell } from "@/components/portal/portal-shell";
import { PortalHeader } from "@/components/portal/portal-header";
import { PortalSubmissionsByForm } from "@/components/portal/portal-submissions-by-form";
import { PortalSection } from "@/components/portal/portal-section";
import { PortalEmptyState } from "@/components/portal/portal-empty-state";
import { PortalFormCard } from "@/components/portal/portal-form-card";
import { PortalFooter } from "@/components/portal/portal-footer";
import {
  iconForRecord,
  portalRecordHref,
} from "@/lib/portal-record";

export const Route = createFileRoute("/$tenantSlug/partner/")({
  component: PartnerPortal,
});

function PartnerPortal() {
  const { tenantSlug } = Route.useParams();
  const qc = useQueryClient();
  const { data: meData, isLoading, isError } = useQuery({
    queryKey: ["partner-me", tenantSlug] as const,
    queryFn: () => loadPartnerMe(tenantSlug),
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
        Loading&hellip;
      </div>
    );
  }

  if (isError || !meData?.partner) {
    return <PartnerLoginForm tenantSlug={tenantSlug} onRequested={() => {}} />;
  }

  return (
    <PartnerDashboard
      tenantSlug={tenantSlug}
      partner={meData.partner}
      onLogout={() => qc.invalidateQueries({ queryKey: ["partner-me", tenantSlug] })}
    />
  );
}

function PartnerLoginForm({
  tenantSlug,
  onRequested,
}: {
  tenantSlug: string;
  onRequested: () => void;
}) {
  const [email, setEmail] = useState("");
  const [requested, setRequested] = useState(false);
  const submit = useMutation({
    mutationFn: () => requestPartnerLogin(tenantSlug, email.trim()),
    onSuccess: () => {
      setRequested(true);
      onRequested();
    },
    onError: () => toast.error("Could not send the magic link"),
  });

  return (
    <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-slate-50 dark:bg-slate-950">
      <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 sm:p-8 space-y-4">
        <header className="space-y-1">
          <h1 className="text-xl font-medium inline-flex items-center gap-2">
            <KeyRound className="size-5" /> Partner sign in
          </h1>
          <p className="text-sm text-slate-500">
            Enter the email address registered with this tenant. We&apos;ll
            send you a one-time sign-in link.
          </p>
        </header>
        {requested ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700 p-3 space-y-1">
            <div className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
              <Mail className="size-4" /> Check your inbox
            </div>
            <p className="text-xs text-emerald-700/80 dark:text-emerald-300/80">
              If your email is registered, you&apos;ll receive a sign-in link
              within a few minutes. The link expires after 72 hours.
            </p>
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit.mutate();
            }}
            className="space-y-3"
          >
            <div className="space-y-1.5">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm"
              />
            </div>
            <button
              type="submit"
              disabled={submit.isPending || !email.includes("@")}
              className="w-full rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-4 py-2 text-sm font-medium disabled:opacity-60"
            >
              {submit.isPending ? "Sending…" : "Send sign-in link"}
            </button>
          </form>
        )}
        <p className="text-xs text-slate-400 text-center pt-3">Powered by ActionaryCRM</p>
      </div>
    </div>
  );
}

function PartnerDashboard({
  tenantSlug,
  partner,
  onLogout,
}: {
  tenantSlug: string;
  partner: PartnerSummary;
  onLogout: () => void;
}) {
  const { data: dealsData, isLoading: dealsLoading } = useQuery({
    queryKey: ["partner-deals", tenantSlug] as const,
    queryFn: () => listPartnerDeals(tenantSlug),
  });
  const deals = dealsData?.deals ?? [];
  const linkedPartner = dealsData?.linked_partner_id ?? null;
  const { data: resources = [], isLoading: resourcesLoading } = useQuery({
    queryKey: ["partner-resources", tenantSlug] as const,
    queryFn: () => listPartnerResources(tenantSlug),
  });
  const { data: forms = [], isLoading: formsLoading } = useQuery({
    queryKey: ["partner-forms", tenantSlug] as const,
    queryFn: () => listPartnerForms(tenantSlug),
  });
  const { data: submissions = [], isLoading: submissionsLoading } = useQuery({
    queryKey: ["partner-submissions", tenantSlug] as const,
    queryFn: () => listPartnerSubmissions(tenantSlug),
    staleTime: 30_000,
  });

  const logout = useMutation({
    mutationFn: () => partnerLogout(tenantSlug),
    onSuccess: () => {
      onLogout();
      window.location.reload();
    },
  });

  return (
    <PortalShell>
      <PortalHeader
        title={partner.full_name || partner.email}
        subtitle={`${partner.company ? `${partner.company} · ` : ""}${partner.email}`}
        actions={
          <button
            type="button"
            onClick={() => logout.mutate()}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <LogOut className="size-4" /> Sign out
          </button>
        }
      />

      {formsLoading ? null : forms.length === 0 ? null : (
        <PortalSection title="Submit" icon={Send}>
          <ul className="grid sm:grid-cols-2 gap-3">
            {forms.map((form) => (
              <PortalFormCard
                key={form.slug}
                tenantSlug={tenantSlug}
                form={form}
                fromPortal="partner"
              />
            ))}
          </ul>
        </PortalSection>
      )}

      <PortalSection title="Your deals">
        {dealsLoading ? (
          <p className="text-sm text-slate-500">Loading&hellip;</p>
        ) : !linkedPartner ? (
          <PortalEmptyState message="Your portal account isn't linked to a partner organisation record yet. Once it is, the deals you sourced will appear here with current stage, value, and the internal owner working them." />
        ) : deals.length === 0 ? (
          <PortalEmptyState
            icon={Briefcase}
            message="No deals yet. When the team accepts one of your submissions and creates a deal, it'll appear here."
          />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            {deals.map((d) => (
              <DealRow key={d.id} deal={d} />
            ))}
          </ul>
        )}
      </PortalSection>

      {resourcesLoading ? (
        <PortalSection title="Resources">
          <p className="text-sm text-slate-500">Loading&hellip;</p>
        </PortalSection>
      ) : resources.length === 0 ? (
        <PortalSection title="Resources">
          <PortalEmptyState message="Nothing has been shared with you yet. Decks, documents, and links from the team will land here." />
        </PortalSection>
      ) : (
        groupByEntity(resources).map(([title, rows]) => (
          <PortalSection key={title} title={title}>
            <ul className="divide-y divide-slate-200 dark:divide-slate-800 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              {rows.map((r) => (
                <ResourceRow
                  key={`${r.entity_slug}:${r.record_id}`}
                  tenantSlug={tenantSlug}
                  resource={r}
                />
              ))}
            </ul>
          </PortalSection>
        ))
      )}

      <PortalSubmissionsByForm
        forms={forms}
        submissions={submissions}
        loading={submissionsLoading}
      />

      <PortalFooter />
    </PortalShell>
  );
}

function DealRow({ deal }: { deal: PartnerDeal }) {
  const sym = deal.currency_symbol || deal.currency_code || "";
  const amount =
    deal.amount != null
      ? `${sym}${Number(deal.amount).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
      : null;
  return (
    <li className="p-3 flex items-start gap-3">
      <div className="flex-1 space-y-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{deal.name}</span>
          {deal.stage_name ? <DealStageBadge deal={deal} /> : null}
        </div>
        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
          {amount ? <span>{amount}</span> : null}
          {deal.close_date ? (
            <span>Close {new Date(deal.close_date).toLocaleDateString()}</span>
          ) : null}
          {deal.probability != null ? <span>{deal.probability}%</span> : null}
          {deal.owner_name || deal.owner_email ? (
            <span>Owner: {deal.owner_name || deal.owner_email}</span>
          ) : null}
        </div>
      </div>
    </li>
  );
}

function DealStageBadge({ deal }: { deal: PartnerDeal }) {
  const tone = deal.is_closed
    ? deal.is_won
      ? "border-emerald-200 text-emerald-700"
      : "border-slate-200 text-slate-500"
    : "border-amber-200 text-amber-700";
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${tone}`}>
      {deal.stage_name}
    </span>
  );
}

function ResourceRow({
  tenantSlug,
  resource,
}: {
  tenantSlug: string;
  resource: PartnerResource;
}) {
  const Icon = iconForRecord(resource);
  const sizeKb = resource.byte_size ? Math.round(resource.byte_size / 1024) : null;
  const entityLabel = resource.entity_title || resource.entity_slug;
  const title = resource.title || `${entityLabel} #${resource.record_id}`;

  // content_assets stay on the presigned-download path — link + file
  // both open the storage-served URL directly. Every other entity
  // routes to its per-entity portal URL (page slug / invoice PDF /
  // future sharable aggregates).
  const isAsset = resource.entity_slug === "content_assets";
  const href = isAsset ? null : portalRecordHref(tenantSlug, resource, "partner");

  const onAssetClick = () => {
    if (!resource.download_url) return;
    void trackResourceDownload(tenantSlug, resource.record_access_id);
    window.open(resource.download_url, "_blank", "noopener,noreferrer");
  };
  const onEntityOpen = () => {
    void trackResourceDownload(tenantSlug, resource.record_access_id);
  };

  const detailLine = _detailLine({
    entityLabel,
    isAsset,
    isLink: resource.is_link,
    mimeType: resource.mime_type,
    sizeKb,
    grantedAt: resource.granted_at,
    amountDisplay: resource.amount_display ?? null,
    status: resource.status ?? null,
  });

  return (
    <li className="p-3 flex items-start gap-3">
      <div className="rounded-md bg-slate-100 dark:bg-slate-800 p-2 mt-0.5">
        <Icon className="size-4 text-slate-500" />
      </div>
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="text-sm font-medium truncate">{title}</div>
        {resource.description ? (
          <p className="text-xs text-slate-500 line-clamp-2">{resource.description}</p>
        ) : resource.subtitle ? (
          <p className="text-xs text-slate-500 line-clamp-2">{resource.subtitle}</p>
        ) : null}
        {resource.message ? (
          <p className="text-xs italic text-slate-500">&ldquo;{resource.message}&rdquo;</p>
        ) : null}
        <p className="text-xs text-slate-400">{detailLine}</p>
      </div>
      {isAsset ? (
        <button
          type="button"
          onClick={onAssetClick}
          disabled={!resource.download_url}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
          title={resource.is_link ? "Open link" : "Download"}
        >
          {resource.is_link ? (
            <>
              <ExternalLink className="size-3" /> Open
            </>
          ) : (
            <>
              <Download className="size-3" /> Download
            </>
          )}
        </button>
      ) : (
        <a
          href={href ?? "#"}
          onClick={onEntityOpen}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          title={`Open ${entityLabel.toLowerCase()}`}
        >
          <ExternalLink className="size-3" /> Open
        </a>
      )}
    </li>
  );
}

function _detailLine({
  entityLabel,
  isAsset,
  isLink,
  mimeType,
  sizeKb,
  grantedAt,
  amountDisplay,
  status,
}: {
  entityLabel: string;
  isAsset: boolean;
  isLink: boolean;
  mimeType: string | null;
  sizeKb: number | null;
  grantedAt: string;
  amountDisplay: string | null;
  status: string | null;
}): string {
  const parts: string[] = [];
  if (isAsset) {
    parts.push(isLink ? "Link" : mimeType ?? "File");
    if (sizeKb != null) parts.push(`${sizeKb} KB`);
  } else {
    parts.push(entityLabel);
    if (amountDisplay) parts.push(amountDisplay);
    if (status) parts.push(status);
  }
  parts.push(`shared ${new Date(grantedAt).toLocaleDateString()}`);
  return parts.join(" · ");
}

/*
 * One section per aggregate, titled by the entity itself. Mirrors the
 * client portal — the server sends each row's `entity_title`, so
 * neither portal holds a list of which entities exist.
 */
function groupByEntity(
  rows: PartnerResource[],
): Array<[string, PartnerResource[]]> {
  const groups = new Map<string, PartnerResource[]>();
  for (const r of rows) {
    const title = r.entity_title || r.entity_slug;
    const bucket = groups.get(title);
    if (bucket) bucket.push(r);
    else groups.set(title, [r]);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}
